import React from 'react';

// Skills component that displays ALL skills with endorsed ones first
export const SkillsDisplay = ({ skillsList }) => {
  if (!skillsList || skillsList.length === 0) {
    return null;
  }

  // Separate endorsed and unendorsed skills
  const endorsedSkills = skillsList.filter(skill => skill.is_endorsed);
  const unendorsedSkills = skillsList.filter(skill => !skill.is_endorsed);

  // Sort endorsed skills by endorsement count (desc)
  endorsedSkills.sort((a, b) => b.endorsement_count - a.endorsement_count);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Skills</h3>
      
      {/* Endorsed Skills Section */}
      {endorsedSkills.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Endorsed Skills ({endorsedSkills.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {endorsedSkills.map((skill, index) => (
              <span
                key={`endorsed-${index}`}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200"
              >
                {skill.name}
                <span className="ml-1 text-xs bg-blue-200 text-blue-700 px-1.5 py-0.5 rounded-full">
                  {skill.endorsement_count}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Unendorsed Skills Section */}
      {unendorsedSkills.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Additional Skills ({unendorsedSkills.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {unendorsedSkills.map((skill, index) => (
              <span
                key={`unendorsed-${index}`}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 border border-gray-200"
              >
                {skill.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="text-xs text-gray-500">
        Total: {skillsList.length} skills ({endorsedSkills.length} endorsed, {unendorsedSkills.length} self-reported)
      </div>
    </div>
  );
};

// Experience component that displays complete work history
export const ExperienceDisplay = ({ experienceHistory }) => {
  if (!experienceHistory || experienceHistory.length === 0) {
    return null;
  }

  const formatDate = (dateObj) => {
    if (!dateObj || typeof dateObj !== 'object') return '';
    if (dateObj.year) {
      return dateObj.month ? `${dateObj.month}/${dateObj.year}` : dateObj.year;
    }
    return '';
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Work Experience</h3>
      
      <div className="space-y-6">
        {experienceHistory.map((job, index) => (
          <div key={index} className="flex gap-4 p-4 border border-gray-200 rounded-lg">
            {/* Company Logo */}
            <div className="flex-shrink-0">
              {job.company_logo_url ? (
                <img
                  src={job.company_logo_url}
                  alt={`${job.company} logo`}
                  className="w-12 h-12 rounded object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs font-medium" style={{display: job.company_logo_url ? 'none' : 'flex'}}>
                {job.company ? job.company.charAt(0) : '?'}
              </div>
            </div>

            {/* Job Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 truncate">
                    {job.title}
                  </h4>
                  <p className="text-base text-gray-700 font-medium">
                    {job.company}
                    {job.is_current && (
                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Current
                      </span>
                    )}
                  </p>
                  <div className="flex items-center text-sm text-gray-500 mt-1">
                    <span>
                      {formatDate(job.start_date)} - {job.is_current ? 'Present' : formatDate(job.end_date)}
                    </span>
                    {job.location && (
                      <>
                        <span className="mx-2">•</span>
                        <span>{job.location}</span>
                      </>
                    )}
                    {job.employment_type && (
                      <>
                        <span className="mx-2">•</span>
                        <span className="capitalize">{job.employment_type}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Job Description */}
              {job.description && (
                <div className="mt-3">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {job.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="text-xs text-gray-500">
        Total: {experienceHistory.length} position{experienceHistory.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

// Education component that displays complete education history
export const EducationDisplay = ({ educationHistory }) => {
  if (!educationHistory || educationHistory.length === 0) {
    return null;
  }

  const formatDate = (dateObj) => {
    if (!dateObj || typeof dateObj !== 'object') return '';
    if (dateObj.year) {
      return dateObj.month ? `${dateObj.month}/${dateObj.year}` : dateObj.year;
    }
    return '';
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Education</h3>
      
      <div className="space-y-4">
        {educationHistory.map((edu, index) => (
          <div key={index} className="flex gap-4 p-4 border border-gray-200 rounded-lg">
            {/* School Logo */}
            <div className="flex-shrink-0">
              {edu.school_logo_url ? (
                <img
                  src={edu.school_logo_url}
                  alt={`${edu.school} logo`}
                  className="w-12 h-12 rounded object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs font-medium" style={{display: edu.school_logo_url ? 'none' : 'flex'}}>
                {edu.school ? edu.school.charAt(0) : '🎓'}
              </div>
            </div>

            {/* Education Details */}
            <div className="flex-1 min-w-0">
              <h4 className="text-lg font-semibold text-gray-900">
                {edu.school}
              </h4>
              
              {edu.degree_name && (
                <p className="text-base text-gray-700 font-medium">
                  {edu.degree_name}
                  {edu.field_of_study && edu.field_of_study !== edu.degree_name && (
                    <span className="text-gray-600"> in {edu.field_of_study}</span>
                  )}
                </p>
              )}

              <div className="flex items-center text-sm text-gray-500 mt-1">
                {edu.start_date && edu.end_date && (
                  <span>
                    {formatDate(edu.start_date)} - {formatDate(edu.end_date)}
                  </span>
                )}
                {edu.grade && (
                  <>
                    <span className="mx-2">•</span>
                    <span>GPA: {edu.grade}</span>
                  </>
                )}
              </div>

              {/* Activities */}
              {edu.activities && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Activities: </span>
                    {edu.activities}
                  </p>
                </div>
              )}

              {/* Description */}
              {edu.description && (
                <div className="mt-2">
                  <p className="text-sm text-gray-700">
                    {edu.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="text-xs text-gray-500">
        Total: {educationHistory.length} school{educationHistory.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

// Location component
export const LocationDisplay = ({ locationData }) => {
  if (!locationData) {
    return null;
  }

  return (
    <div className="flex items-center text-sm text-gray-600">
      <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
      <span>
        {locationData.full_location || locationData.full || [locationData.city, locationData.state, locationData.country].filter(Boolean).join(', ')}
      </span>
      {locationData.country_code && (
        <span className="ml-2 text-xs text-gray-400">({locationData.country_code})</span>
      )}
    </div>
  );
};